import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TokenService } from './token.service';
import { ContextService } from '../../context/services/context.service';
import { StandaloneAuthService } from './standalone-auth.service';
import { STORAGE_KEYS, LEGACY_STORAGE_KEYS } from '../../../shared/constants';

/**
 * Adiciona o token Bearer e headers de contexto em todas as requisições HTTP.
 * Fontes de token, em ordem: localStorage (bridge) → StandaloneAuthService → TokenService.
 */
@Injectable()
export class AuthInterceptorService implements HttpInterceptor {

  constructor(
    private tokenService: TokenService,
    private contextService: ContextService,
    private standaloneAuth: StandaloneAuthService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // A troca de code/refresh_token por access_token NUNCA passa por getLatestToken():
    // essa request é o que produz o token.
    if (req.url.includes('/auth/oauth2/token')) {
      return next.handle(req);
    }

    return this.proceedWithRequest(req, next, this.getLatestToken());
  }

  /** PRIORIDADE: localStorage (bridge) → StandaloneAuthService → TokenService. */
  private getLatestToken(): string | null {
    // Fallback de leitura na chave antiga (__pwa_bridge_token) por 1 release — TODO remover
    const token = localStorage.getItem(STORAGE_KEYS.BRIDGE_TOKEN) ||
      localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_TOKEN);
    if (token) {
      return token;
    }

    const standaloneToken = this.standaloneAuth.getAccessToken();
    if (standaloneToken) {
      return standaloneToken;
    }

    return this.tokenService.getAccessToken();
  }

  private proceedWithRequest(req: HttpRequest<unknown>, next: HttpHandler, token: string | null): Observable<HttpEvent<unknown>> {
    if (token) {
      const headers: Record<string, string> = {};
      headers['Authorization'] = `Bearer ${token}`;

      // Fallback de leitura na chave antiga (__pwa_bridge_app_context) por 1 release — TODO remover
      const appContext = localStorage.getItem(STORAGE_KEYS.BRIDGE_APP_CONTEXT) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_APP_CONTEXT) || this.contextService.getAppContext();

      // X-Skip-App-Context: usado pelo modal de contexto ao trocar de entidade, evita enviar
      // app-context desatualizado que conflitaria com o novo User-Access.
      const skipAppContext = req.headers.has('X-Skip-App-Context');

      // Só sobrescreve app-context se o request original não trouxer um específico
      // (ex: ao carregar anos letivos). app-context não deve conter turma (vai na query string).
      const hasAppContextHeader = req.headers.has('app-context');

      if (appContext && !hasAppContextHeader && !skipAppContext) {
        headers['app-context'] = appContext;
      } else if (hasAppContextHeader) {
        const originalValue = req.headers.get('app-context');
        if (originalValue) {
          headers['app-context'] = originalValue;
        }
      }

      // Fallback de leitura na chave antiga (__pwa_bridge_user_access) por 1 release — TODO remover
      const userAccess = localStorage.getItem(STORAGE_KEYS.BRIDGE_USER_ACCESS) ||
        localStorage.getItem(LEGACY_STORAGE_KEYS.BRIDGE_USER_ACCESS) || this.contextService.getUserAccess();

      // Só sobrescreve User-Access se o request original não trouxer um específico (ex: troca de entidade)
      const hasUserAccessHeader = req.headers.has('User-Access') || req.headers.has('user-access');

      // Endpoints de infraestrutura da plataforma Betha NÃO aceitam User-Access
      // (qualquer valor, válido ou stale, causa 504 Gateway Timeout).
      const isPlatformEndpoint = req.url.includes('plataforma-autorizacoes') ||
                                 req.url.includes('login-session-verifier') ||
                                 req.url.includes('my-preferences.educacao');

      // Endpoints de plataforma não permitem Cache-Control no CORS (causaria preflight);
      // ngsw-bypass ignora o cache mesmo no SW antigo (com dataGroups).
      if (!isPlatformEndpoint) {
        headers['Cache-Control'] = 'no-store';
        headers['ngsw-bypass'] = 'true';
      }

      if (userAccess && !hasUserAccessHeader && !isPlatformEndpoint) {
        // 'User-Access' maiúscula: backend é case-sensitive, confirmado no AngularJS
        // (contexto-estabelecimento.service.js, contexto-servidor.service.js)
        headers['User-Access'] = userAccess;
      } else if (hasUserAccessHeader) {
        const originalValue = req.headers.get('User-Access') || req.headers.get('user-access');
        if (originalValue) {
          headers['User-Access'] = originalValue;
        }
      }

      let finalHeaders = req.headers;
      if (finalHeaders.has('X-Skip-App-Context')) {
        finalHeaders = finalHeaders.delete('X-Skip-App-Context');
      }

      Object.keys(headers).forEach(key => {
        finalHeaders = finalHeaders.set(key, headers[key]);
      });

      // my-preferences usa Access-Control-Allow-Origin: * — incompatível com withCredentials.
      // Bearer token já basta pra autenticação.
      const needsCredentials = !isPlatformEndpoint;

      const authReq = req.clone({
        headers: finalHeaders,
        withCredentials: needsCredentials
      });

      // Endpoints de plataforma: ngsw-bypass via query param (header causaria CORS preflight)
      // Demais endpoints: ngsw-bypass via header (já incluído em `headers` acima)
      if (isPlatformEndpoint) {
        const bypassParams = authReq.params.set('ngsw-bypass', '');
        return next.handle(authReq.clone({ params: bypassParams }));
      }
      return next.handle(authReq);
    }

    // Prosseguir sem token - AuthErrorInterceptor tratará 401 se necessário
    const isPlat = req.url.includes('plataforma-autorizacoes') || req.url.includes('login-session-verifier') || req.url.includes('my-preferences.educacao');
    if (isPlat) {
      const bypassParams = req.params.set('ngsw-bypass', '');
      return next.handle(req.clone({ params: bypassParams }));
    }
    return next.handle(req.clone({ setHeaders: { 'Cache-Control': 'no-store', 'ngsw-bypass': 'true' } }));
  }
}
